import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";
import { useSubmitButtonManager } from "../mixins/useSubmitButtonManager";
import { useTemplateManager } from "../mixins/useTemplateManager";

export default class extends Controller {
  static targets = [
    "display", 
    "displayElementTemplate", 
    "progressTemplate", 
    "successTemplate", 
    "warningTemplate", 
    "issueItemTemplate"
  ];
  static values = { 
    policyReference: String,
    locale: String,
    supportedReasons: Array
  };

  connect() {
    // Add submit button management capabilities
    useSubmitButtonManager(this);
    // Add template management capabilities
    useTemplateManager(this);
    
    this.subscription = null;
    // Track verification requests that need to be sent when connected to the ActionCable subscription
    this.queuedForVerification = new Set();

    // Listen for the documentUploaded event dispatched by direct_upload_controller
    this.documentUploadedHandler = this.documentUploaded.bind(this)
    this.element.addEventListener('direct-upload:documentUploaded', this.documentUploadedHandler)
    
    // Listen for the documentRemoved event dispatched by direct_upload_controller
    this.documentRemovedHandler = this.documentRemoved.bind(this)
    this.element.addEventListener('direct-upload:documentRemoved', this.documentRemovedHandler)
  }
  
  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.element.removeEventListener('direct-upload:documentUploaded', this.documentUploadedHandler)
    this.element.removeEventListener('direct-upload:documentRemoved', this.documentRemovedHandler)
  }

  shouldTriggerVerification() {
    const verificationReasons = this.supportedReasonsValue;
    const reasonValue = document.querySelector('#cancellation_request_reason')?.value;
    
    return verificationReasons.includes(reasonValue);
  }
  
  documentUploaded(event) {
    if (!this.shouldTriggerVerification()) return;

    const { docstoreId } = event.detail;
    this.ensureSubscription();
    
    // Send verification request immediately if connected, otherwise queue it
    this.sendOrQueueVerification(docstoreId);
  }

  documentRemoved(event) {
    const { docstoreId } = event.detail;
    
    // Remove from queued verifications if it was queued
    this.queuedForVerification.delete(docstoreId);

    // Re-enable submit button if this document was being verified
    this.enableSubmitButton(`doc-verify-${docstoreId}`);

    // Abort backend polling/verification
    this.abortVerification(docstoreId);
  }


  getFileVerificationContainer(docstoreId) {
    // Find the file item with matching docstore ID and get its sibling verification container
    const fileItem = this.element.querySelector(`[data-docstore-id="${docstoreId}"]`);
    if (!fileItem) return null;

    const parentRow = fileItem.closest('.direct-upload-file-item');
    return parentRow ? parentRow.querySelector('[data-verification-container]') : null;
  }

  ensureSubscription() {
    if (this.subscription) {
      return;
    }

    this.subscription = consumer.subscriptions.create({
      channel: "DocVerifyChannel", 
      policy_reference: this.policyReferenceValue,
      locale: this.localeValue
    }, {
      connected: () => {
        this.sendPendingVerifications();
      },

      disconnected: () => {
      },

      rejected: () => {
        console.error("ERROR - DocVerifyChannel subscription was rejected");
        this.displayGenericError("Failed to connect to document verification service. Please refresh the page and try again.");
      },

      received: (data) => {
        this.updateDisplay(data);
      }
    });
  }

  sendOrQueueVerification(docstoreId) {
    // Start tracking this verification and disable the submit button
    this.startVerificationTracking(docstoreId);
    
    if (this.subscription && this.subscription.consumer.connection.isOpen()) {
      this.requestVerification(docstoreId);
    } else {
      this.queuedForVerification.add(docstoreId);
    }
  }

  requestVerification(docstoreId) {
    if (!this.subscription) {
      console.error("ERROR - No subscription available for verification request");
      this.displayGenericError("Failed to contact the document verification service. Please refresh the page and try again.");
      return;
    }

    try {
      this.subscription.perform('extract_and_classify', {
        document_id: docstoreId,
        policy_reference: this.policyReferenceValue
      });
    } catch (error) {
      console.error("ERROR - failed to perform extract_and_classify:", error);
      this.displayGenericError("Failed to initiate document verification. Please refresh the page and try again.");
    }
  }

  abortVerification(docstoreId) {
    if (!this.subscription) {
      return;
    }
    
    try {
      this.subscription.perform('abort_verification', {
        document_id: docstoreId
      });
    } catch (error) {
      console.error("ERROR - failed to abort verification:", error);
    }
  }

  updateDisplay(data) {
    const contentElement = this.getFileVerificationContainer(data.document_id);
    // Ignore updates without a matching element; user might have removed a document
    if (!contentElement) return;

    if (data.status !== 'pending') {
      // Verification no longer in progress, so re-enable the submit button
      this.endVerificationTracking(data.document_id);
    }

    if (data.status === 'pending') {
      this.displayProgress(data, contentElement);
    } else if (data.status === 'complete') {
      // Display results only if the document type matches, and some fields are incorrect - otherwise clear them
      if (data.document_type_matches && !data.success) {
        this.displayWarnings(data.comparison_result, contentElement);
      } else {
        contentElement.innerHTML = '';
      }
    } else {
      console.error(`Document verification error: ${data.message}`);
      contentElement.innerHTML = '';
    }
  }

  displayProgress(data, contentElement) {
    const progressPct = data.progress_pct || 0;
    const message = data.message || 'Verifying document...';
    
    this.createAndAppendTemplate({
      template: this.progressTemplateTarget,
      container: contentElement,
      content: {
        '[data-message-placeholder]': { textContent: message },
        '[data-progress-bar]': {
          style: { width: `${progressPct}%` }
        }
      },
      replaceContent: true
    });
  }

  displayWarnings(comparisonResult, contentElement) {
    const issues = this.buildIssuesList(comparisonResult);

    // First, create and append the warning template
    const warningElement = this.createAndAppendTemplate({
      template: this.warningTemplateTarget,
      container: contentElement,
      content: {},
      replaceContent: true
    });

    // Get the issues list container from the warning template
    const issuesListContainer = warningElement.querySelector('[data-issues-list]');
    if (issuesListContainer) {
      issues.forEach(issue => {
        this.createAndAppendTemplate({
          template: this.issueItemTemplateTarget,
          container: issuesListContainer,
          content: {
            '[data-issue-text]': { textContent: issue }
          }
        });
      });
    }
  }

  buildIssuesList(comparisonResult) {
    const issues = [];
    
    if (comparisonResult) {
      Object.keys(comparisonResult).forEach(key => {
        const result = comparisonResult[key];
        if (result?.display_message) {
          issues.push(result.display_message);
        }
      });
    }

    return issues;
  }

  sendPendingVerifications() {
    for (const docstoreId of this.queuedForVerification) {
      this.requestVerification(docstoreId);
    }
    this.queuedForVerification.clear();
  }

  displayGenericError(message) {
    // For generic errors, create a temporary element in the display target
    const errorElement = document.createElement('div');
    errorElement.className = 'doc-verify-error alert alert-danger';
    errorElement.innerHTML = `<strong>Document Verification Error</strong><p>${message}</p>`;
    this.displayTarget.appendChild(errorElement);
  }

  startVerificationTracking(docstoreId) {
    this.disableSubmitButton(`doc-verify-${docstoreId}`, 'Verifying...');
  }

  endVerificationTracking(docstoreId) {
    this.enableSubmitButton(`doc-verify-${docstoreId}`);
  }
} 
